import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import isToday from 'date-fns/isToday';
import isTomorrow from 'date-fns/isTomorrow';
import subDays from 'date-fns/subDays';
import subHours from 'date-fns/subHours';
import isBefore from 'date-fns/isBefore';
import setSeconds from 'date-fns/setSeconds';
import setMilliseconds from 'date-fns/setMilliseconds';
import isEqual from 'date-fns/isEqual';
import isWithinInterval from 'date-fns/isWithinInterval';
import startOfDay from 'date-fns/startOfDay';
import addSeconds from 'date-fns/addSeconds';
import moment from 'moment';
import 'moment-timezone';
import { DEFAULT_FORMAT_TIME_TEXTS } from '@dtvgo/constants';

/* External */

/* Constants */
var DEFAULT_FORMAT_TIME = 'HH:mm';

/**
 * Returns a string with the correct format of the date in the given timeZone.
 * @param {date} date - The date starts content.
 * @param {string} formatToUse - format to implement
 * @param {string} timeZone - timeZone to display
 * @returns {string} The formatted time
 */

function formatDate(date, formatToUse, timeZone) {
  if (timeZone && moment.tz.zone(timeZone)) {
    return moment.tz(date, timeZone).format(formatToUse);
  }
  return format(date, formatToUse);
}

/**
 * @method formatDifference
 * @description Method that format difference date as xxd xxh xxmins.
 * @param {Date} startDate opcional, for default is current date.
 * @param {Date} endDate
 */
function formatTimeDifference(currentTime, endDate, formatTimeTexts, validateStartedEndedRightNow) {
  if (endDate) {
    var start = currentTime || new Date();
    var timeTexts = formatTimeTexts || DEFAULT_FORMAT_TIME_TEXTS;
    var end = endDate;
    var resultText = [];
    var _timeTexts$time = timeTexts.time,
      minutes = _timeTexts$time.minutes,
      _timeTexts$time$hours = _timeTexts$time.hours,
      txtHours = _timeTexts$time$hours.plural,
      txtHour = _timeTexts$time$hours.singular;
    var diffInDay = differenceInDays(end, start);
    if (diffInDay > 0) {
      resultText.push("".concat(diffInDay, " d"));
      end = subDays(end, diffInDay);
    }
    var diffInHours = differenceInHours(end, start);
    if (diffInHours > 0) {
      resultText.push("".concat(diffInHours, " ").concat(diffInHours > 1 ? txtHours : txtHour));
      end = subHours(end, diffInHours);
    }
    var diffInSeconds = differenceInSeconds(end, start);
    var diffInMinutes = Math.ceil(diffInSeconds / 60);
    if (diffInMinutes > 0) {
      resultText.push("".concat(diffInMinutes, " ").concat(minutes));
    } else if (validateStartedEndedRightNow && diffInSeconds < 60) {
      resultText.push("0 ".concat(minutes));
    }
    return resultText.join(' ');
  }
  return '';
}
function formatPastOrFutureTime(currentTime, endDate, finishDate, formatTimeTexts, validateStartedEndedRightNow, timeZone) {
  if (endDate) {
    var start = currentTime || new Date();
    var timeTexts = formatTimeTexts || DEFAULT_FORMAT_TIME_TEXTS;
    var end = endDate;
    var resultText = [];
    var startsAt = timeTexts.texts.startsAt;
    var minutes = timeTexts.time.minutes;
    var _timeTexts$time$day = timeTexts.time.day,
      today = _timeTexts$time$day.today,
      tomorrow = _timeTexts$time$day.tomorrow;
    var textTime = formatDate(end, DEFAULT_FORMAT_TIME, timeZone);
    if (start > finishDate) {
      return "".concat(today, " ").concat(textTime);
    }
    var diffInHours = differenceInHours(end, start);
    if (diffInHours > 0) {
      if (isSameDay(end, start)) {
        return "".concat(today, " ").concat(textTime);
      }
      var diffInDay = differenceInCalendarDays(end, start);
      var formatToUse = moment.tz.zone(timeZone) ? 'dddd' : 'EEEE';
      var textDay = diffInDay === 1 ? tomorrow : formatDate(end, formatToUse, timeZone);
      var textDayCapitalized = textDay.charAt(0).toUpperCase() + textDay.slice(1);
      return "".concat(textDayCapitalized, " ").concat(textTime);
    }
    end = subHours(end, diffInHours);
    resultText.push(startsAt);
    var diffInSeconds = differenceInSeconds(end, start);
    var diffInMinutes = Math.ceil(diffInSeconds / 60);
    if (diffInMinutes > 0) {
      resultText.push("".concat(diffInMinutes, " ").concat(minutes));
    } else if (validateStartedEndedRightNow && diffInSeconds < 60) {
      resultText.push("0 ".concat(minutes));
    }
    return resultText.join(' ');
  }
  return '';
}
function isBeforeSameOrAfterDate(date) {
  if (!date) return null;
  var currentDate = setSeconds(setMilliseconds(new Date(), 0), 0);
  var dateToValidate = setSeconds(setMilliseconds(date, 0), 0);
  if (isBefore(dateToValidate, currentDate)) {
    return -1;
  }
  if (isEqual(currentDate, dateToValidate)) {
    return 0;
  }
  return 1;
}
function isWithinIntervalDate(startDate, endDate) {
  var dateToValidate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : new Date();
  if (startDate && endDate) {
    return isWithinInterval(dateToValidate, {
      start: startDate,
      end: endDate
    });
  }
  return null;
}
function isTodayOrLater(date, formatTexts) {
  var formatTimeTexts = formatTexts || DEFAULT_FORMAT_TIME_TEXTS;
  var day = new Date(date);
  var formatDay = format(day, 'i');
  var _formatTimeTexts$time = formatTimeTexts.time.day,
    today = _formatTimeTexts$time.today,
    tomorrow = _formatTimeTexts$time.tomorrow,
    monday = _formatTimeTexts$time.monday,
    tuesday = _formatTimeTexts$time.tuesday,
    wednesday = _formatTimeTexts$time.wednesday,
    thursday = _formatTimeTexts$time.thursday,
    friday = _formatTimeTexts$time.friday,
    saturday = _formatTimeTexts$time.saturday,
    sunday = _formatTimeTexts$time.sunday;
  if (isToday(day)) {
    return today;
  }
  if (isTomorrow(day)) {
    return tomorrow;
  }
  switch (formatDay) {
    case '1':
      return monday;
    case '2':
      return tuesday;
    case '3':
      return wednesday;
    case '4':
      return thursday;
    case '5':
      return friday;
    case '6':
      return saturday;
    case '7':
      return sunday;
    default:
      return '';
  }
}
function isTodayOrTomorrowDay(date, formatTexts) {
  var formatTimeTexts = formatTexts || DEFAULT_FORMAT_TIME_TEXTS;
  var day = new Date(date);
  var _formatTimeTexts$time2 = formatTimeTexts.time.day,
    today = _formatTimeTexts$time2.today,
    tomorrow = _formatTimeTexts$time2.tomorrow;
  if (isToday(day)) {
    return today;
  }
  if (isTomorrow(day)) {
    return tomorrow;
  }
  return '';
}
function formatTime(dateTime) {
  var formatToUse = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_FORMAT_TIME;
  var timeZone = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  return formatDate(new Date(dateTime), formatToUse, timeZone);
}
function formatStartTime(startTime) {
  var formatToUse = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_FORMAT_TIME;
  var timeZone = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  if (startTime) {
    return formatTime(startTime, formatToUse, timeZone);
  }
  return '';
}
function formatStartAndEndTime(startTime, endTime) {
  var formatToUse = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_FORMAT_TIME;
  var timeZone = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  if (startTime && endTime) {
    return "".concat(formatTime(startTime, formatToUse, timeZone), " - ").concat(formatTime(endTime, formatToUse, timeZone));
  }
  return '';
}
function startsAtOrEndsInText(startTime, endTime) {
  var formatTexts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_FORMAT_TIME_TEXTS;
  var timeZone = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  if (!startTime) {
    return '';
  }
  var currentDate = setSeconds(setMilliseconds(new Date(), 0), 0);
  if (isBeforeSameOrAfterDate(startTime) === -1) {
    var _formatTexts$texts;
    return endTime && "".concat((formatTexts === null || formatTexts === void 0 ? void 0 : (_formatTexts$texts = formatTexts.texts) === null || _formatTexts$texts === void 0 ? void 0 : _formatTexts$texts.endsIn) || '', " ").concat(formatTimeDifference(currentDate, endTime, null, true)) || '';
  }
  if (differenceInHours(startTime, currentDate) < 1) {
    var _formatTexts$texts2;
    return "".concat((formatTexts === null || formatTexts === void 0 ? void 0 : (_formatTexts$texts2 = formatTexts.texts) === null || _formatTexts$texts2 === void 0 ? void 0 : _formatTexts$texts2.startsAt) || '', " ").concat(formatTimeDifference(currentDate, startTime, null, true));
  }
  var day = isTodayOrLater(startTime, formatTexts);
  return "".concat(day, " ").concat(formatDate(startTime, DEFAULT_FORMAT_TIME, timeZone));
}

/**
 * Returns a string with the correct format. If time is greater or equal than
 * 1 hour the format will be H:mm:ss if not, it will be mm:ss
 * @param {number} time - The time to format in seconds
 * @returns {string} The formatted time
 */
function formatSeconds(time) {
  var dateFormat = time >= 3600 ? 'H:mm:ss' : 'mm:ss';
  if ((time || time === 0) && time !== Number.POSITIVE_INFINITY) {
    return format(addSeconds(startOfDay(new Date()), time), dateFormat);
  }
  return DEFAULT_FORMAT_TIME;
}

/**
 * @method getRemainingTimeFormated
 * @description Method that return the remaining time text with the correct format.
 * @param {number} currentTime
 * @param {number} duration
 */
function getRemainingTimeFormated(currentTime, duration) {
  var timeLeft = (duration !== null && duration !== void 0 ? duration : 0) - (currentTime !== null && currentTime !== void 0 ? currentTime : 0);
  return formatSeconds(timeLeft);
}

export { formatDate, formatPastOrFutureTime, formatSeconds, formatStartAndEndTime, formatStartTime, formatTime, formatTimeDifference, getRemainingTimeFormated, isBeforeSameOrAfterDate, isTodayOrLater, isTodayOrTomorrowDay, isWithinIntervalDate, startsAtOrEndsInText };
//# sourceMappingURL=index.js.map
